import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Shield, Globe2, Package, Ship, Factory, CheckCircle2, MapPin, Award, Users, Container, Quote, Download, TrendingUp } from "lucide-react";
import { IMG, IMG_ID, HERO_DESKTOP, HERO_MOBILE, SERVICES, CLUSTERS, PROCESS_STEPS, TESTIMONIALS, TRUST_MARQUEE, SOURCING_CATEGORIES } from "@/lib/content";
import { useCMSList } from "@/lib/cms";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import ResponsiveImg from "@/components/ResponsiveImg";
import CalendlyInline from "@/components/CalendlyInline";

export default function Home() {
  const caseStudies = useCMSList("case_studies");
  const press = useCMSList("press_mentions");
  const [freight, setFreight] = useState({ featured: [], last_updated: null, total_corridors: 0 });
  useEffect(() => {
    api.get("/freight/rates/summary").then((r) => setFreight(r.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="home-page">
      <SEO
        title="Fortune Sourcings · Premium China Sourcing Partner Since 1991"
        description="Fortune Sourcings — third-generation, on-ground in China since 1991. Premium sourcing for furniture, hotel FF&E, machinery, and building materials for serious importers in 40+ countries."
        path="/"
      />
      {/* 1. HERO  —  art-direction via <picture>: 800w WebP on phones, 1920w on desktop.
              Saves ~70KB on 3G vs. shipping the desktop hero to mobile. */}
      <Helmet>
        {/* Responsive preload — browser picks the correct candidate based on media query */}
        <link
          rel="preload" as="image"
          href={HERO_DESKTOP}
          imagesrcset={`${HERO_MOBILE} 800w, ${HERO_DESKTOP} 1920w`}
          imagesizes="100vw"
          fetchpriority="high"
        />
      </Helmet>
      <section className="relative min-h-[92vh] bg-[#1C3E5F] overflow-hidden flex items-end" data-testid="hero-section">
        <picture>
          <source media="(max-width: 768px)" srcSet={HERO_MOBILE} type="image/webp" />
          <source srcSet={HERO_DESKTOP} type="image/webp" />
          <img
            src={HERO_DESKTOP}
            alt="Container port"
            width="1920"
            height="1280"
            fetchpriority="high"
            loading="eager"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
        </picture>
        <div className="absolute inset-0 hero-overlay grain" />
        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 lg:px-10 pb-24 pt-40">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-8">
              <span className="gold-rule"></span>
              <span className="eyebrow text-[#B8965A]">Est. 1991 · Third Generation · On-Ground In China</span>
            </div>
            <h1 className="font-serif text-white text-[56px] sm:text-[72px] lg:text-[96px] leading-[0.95] tracking-tight" data-testid="hero-heading">
              The operational partner<br/>for serious importers<br/><span className="italic text-[#B8965A]">sourcing from China.</span>
            </h1>
            <p className="text-white/75 text-lg lg:text-xl max-w-2xl mt-10 leading-relaxed">
              Fortune Sourcings runs on-ground procurement, factory audits, quality control and export logistics for importers worldwide, across furniture, hotel FF&amp;E, machinery, mining equipment, road-construction materials (geogrid, geotextile and geocomposite), BOPET films, technical fabrics, mobile accessories, stationery and the wider Yiwu commodity universe. If it ships from China, we run the ground.
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link to="/submit-rfq" data-testid="hero-rfq-cta" className="btn-primary">Submit RFQ <ArrowRight size={16}/></Link>
              <Link to="/book-consultation" data-testid="hero-consultation-cta" className="btn-outline">Book Consultation</Link>
              <Link to="/china-business-trips" className="btn-outline">Plan China Visit</Link>
            </div>
          </div>
        </div>

        {/* Corner metric */}
        <div className="hidden lg:block absolute bottom-10 right-10 text-right z-10">
          <div className="number-display text-[120px]">1991</div>
          <div className="eyebrow text-[#B8965A] -mt-3">Third Generation Import &amp; Export</div>
        </div>
      </section>

      {/* 2. TRUST STRIP (ticker) */}
      <section className="bg-[#12283F] py-5 overflow-hidden border-y border-[#B8965A]/20" data-testid="trust-strip">
        <div className="ticker-track whitespace-nowrap">
          {[...TRUST_MARQUEE, ...TRUST_MARQUEE].map((t, i) => (
            <span key={i} className="eyebrow text-[#B8965A]/80 flex items-center gap-4">
              <span>{t}</span>
              <span className="w-1 h-1 rounded-full bg-[#B8965A]/40"></span>
            </span>
          ))}
        </div>
      </section>

      {/* 2b. STATS BAR (interactive) */}
      <section className="bg-white border-b border-[#E2E8F0]" data-testid="stats-bar">
        <div className="max-w-[1400px] mx-auto grid grid-cols-2 md:grid-cols-4 divide-x divide-[#E2E8F0]">
          {[
            { icon: Globe2,   k: "25+",     label: "Countries shipped to",       sub: "Across five continents" },
            { icon: Factory,  k: "2,000+",  label: "China factories worked with", sub: "Audited & continuously reassessed" },
            { icon: Container,k: "5,000+",  label: "Containers shipped",         sub: "And counting — daily lanes across ports" },
            { icon: Award,    k: "1991",    label: "Established",                sub: "Third generation, family operated" },
          ].map((s, i) => (
            <div key={i} className="p-6 lg:p-8 hover:bg-[#F7F8FA] transition-colors group cursor-default" data-testid={`stat-${i}`}>
              <s.icon size={20} className="text-[#B8965A] mb-3 group-hover:scale-110 transition-transform" strokeWidth={1.5}/>
              <div className="font-serif text-[#1C3E5F] text-4xl lg:text-5xl leading-none">{s.k}</div>
              <div className="eyebrow text-[#1C3E5F] mt-2">{s.label}</div>
              <div className="text-[11px] text-[#6B7280] mt-1">{s.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 2c. (offices moved to footer per client request) */}
      {press && press.length === 0 && null}

      {/* 3. PROBLEM / PAIN */}
      <section className="bg-white py-20" data-testid="problem-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-rule"></span>
              <span className="eyebrow text-[#1C3E5F]">The Problem</span>
            </div>
            <h2 className="font-serif text-[#1C3E5F] text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Sourcing from China fails for structural reasons, not bad luck.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-8 text-[17px] leading-relaxed text-[#1C3E5F]/75">
            <p>Most importers discover China is expensive only after they have paid for it. Samples that do not match production. Factories that sub-contract mid-order. Specifications that drift without notice. Certifications that do not pass destination inspection. Containers held at origin for documentation gaps.</p>
            <p>None of this is bad luck. It is what happens when you try to manage a supply chain 5,000&nbsp;km away through email threads and platform messaging.</p>
            <div className="grid sm:grid-cols-2 gap-4 pt-4">
              {[
                "Ghost factories that never owned the tooling",
                "Samples from one factory, production from another",
                "Certificates that fail destination customs",
                "Hidden charges emerging at destination port",
              ].map((p) => (
                <div key={p} className="flex gap-3 items-start">
                  <div className="w-5 h-5 mt-0.5 border border-[#B8965A] flex items-center justify-center shrink-0">
                    <span className="w-1.5 h-1.5 bg-[#B8965A]"></span>
                  </div>
                  <p className="text-sm text-[#1C3E5F]/80">{p}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 4. WHY FORTUNE / SOLUTION */}
      <section className="bg-[#1C3E5F] py-20 relative overflow-hidden" data-testid="why-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-start mb-12">
            <div className="lg:col-span-6">
              <div className="flex items-center gap-3 mb-6">
                <span className="gold-rule"></span>
                <span className="eyebrow text-[#B8965A]">The Fortune Model</span>
              </div>
              <h2 className="font-serif text-white text-5xl lg:text-6xl leading-[1.05]">Operational control, not agency brokering.</h2>
            </div>
            <div className="lg:col-span-6 text-white/70 text-lg leading-relaxed">
              We are not a middleman. We are not a freelance agent. We are a third-generation import and export business with permanent offices in Foshan, Guangzhou and Yiwu (China), Siliguri (India — head office) and Kathmandu &amp; Dhulabari (Nepal). Every order is physically audited, inspected and consolidated under our control, documented, not promised.
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-[#B8965A]/20">
            {[
              { icon: Factory, k: "On-Ground In China", v: "Permanent offices in Foshan, Guangzhou and Yiwu. Head office in Siliguri (India). Nepal offices in Kathmandu and Dhulabari (Jhapa). Not an outsourced inspector on call." },
              { icon: Shield, k: "AQL Inspection", v: "Sampling plans to the ANSI Z1.4 standard. No container seals without QC sign-off." },
              { icon: Ship, k: "In-House Freight", v: "Direct negotiation with shipping lines. Documentation desk handles CO, FORM-E, BL." },
              { icon: Globe2, k: "Global Reach", v: "We ship from China to importers worldwide, destination compliance pre-validated for each market." },
            ].map((c) => (
              <div key={c.k} className="border-b border-r border-[#B8965A]/20 p-8 md:p-10">
                <c.icon className="text-[#B8965A] mb-6" size={28} strokeWidth={1.25}/>
                <div className="font-serif text-white text-2xl mb-3">{c.k}</div>
                <p className="text-white/60 text-[13.5px] leading-relaxed">{c.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. WHAT WE SOURCE */}
      <section className="bg-white py-20" data-testid="services-grid">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="gold-rule"></span>
                <span className="eyebrow text-[#1C3E5F]">What We Source</span>
              </div>
              <h2 className="font-serif text-[#1C3E5F] text-5xl lg:text-6xl leading-[1.05]">If it ships from China,<br/>we run the ground.</h2>
            </div>
            <Link to="/services" className="link-underline text-[#1C3E5F] text-sm uppercase tracking-[0.18em] font-semibold">All Services <ArrowRight size={14} className="inline"/></Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.slice(0, 6).map((s, i) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="group relative block bg-[#1C3E5F] overflow-hidden aspect-[4/3]"
              >
                <img loading="lazy" decoding="async" src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-75 group-hover:scale-105 transition-all duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#12283F] via-[#12283F]/30 to-transparent"/>
                <div className="relative z-10 h-full flex flex-col justify-between p-8">
                  <div className="eyebrow text-[#B8965A]">{s.tag}</div>
                  <div>
                    <div className="font-serif text-white text-3xl lg:text-4xl leading-tight mb-3">{s.title}</div>
                    <p className="text-white/70 text-sm leading-relaxed max-w-sm">{s.short}</p>
                    <div className="mt-5 inline-flex items-center gap-2 text-[#B8965A] text-xs uppercase tracking-[0.18em] font-semibold">
                      Explore <ArrowRight size={14}/>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 5b. CATEGORIES WE SHIP, diversified breadth */}
      <section className="bg-[#12283F] py-16 relative overflow-hidden" data-testid="categories-bar">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-8">
            <span className="gold-rule"></span>
            <span className="eyebrow text-[#B8965A]">Categories We Ship</span>
          </div>
          <h2 className="font-serif text-white text-3xl lg:text-4xl mb-10 max-w-3xl leading-tight">
            Everything a serious importer sources from China — one accountable partner.
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-[#B8965A]/15 border border-[#B8965A]/15">
            {SOURCING_CATEGORIES.map((c) => (
              <div key={c.name} className="bg-[#12283F] hover:bg-[#1C3E5F] transition-colors p-5" data-testid={`cat-${c.name.replace(/[^A-Za-z]/g, '')}`}>
                <div className="font-serif text-white text-lg leading-tight">{c.name}</div>
                <div className="text-[11.5px] text-white/55 mt-2 leading-relaxed">{c.note}</div>
              </div>
            ))}
          </div>
          <p className="text-white/50 text-xs mt-6 max-w-2xl">…and a long tail of niche categories sourced on bespoke briefs. If your category is not listed, ask, we have likely shipped it.</p>
        </div>
      </section>

      {/* 6. PROCESS */}
      <section className="bg-[#F8FAFC] py-20" data-testid="process-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-20">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-rule"></span>
              <span className="eyebrow text-[#1C3E5F]">The Process</span>
            </div>
            <h2 className="font-serif text-[#1C3E5F] text-5xl lg:text-6xl leading-[1.05]">Eight stages. Signed off in writing. Every time.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E2E8F0]">
            {PROCESS_STEPS.map((p) => (
              <div key={p.step} className="bg-white p-8 min-h-[260px] flex flex-col">
                <div className="number-display text-6xl mb-4">{p.step}</div>
                <div className="font-serif text-[#1C3E5F] text-xl mb-2">{p.title}</div>
                <p className="text-sm leading-relaxed text-[#1C3E5F]/65">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. EXPORT LOGISTICS */}
      <section className="bg-[#1C3E5F] py-20 relative overflow-hidden" data-testid="logistics-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <ResponsiveImg srcId={IMG_ID.port} alt="Shipping logistics" className="w-full aspect-[5/4] object-cover" />
          </div>
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-rule"></span>
              <span className="eyebrow text-[#B8965A]">Export Logistics</span>
            </div>
            <h2 className="font-serif text-white text-5xl lg:text-6xl leading-[1.05] mb-8">From factory floor to your warehouse, one desk.</h2>
            <p className="text-white/70 text-lg leading-relaxed mb-8">
              Most sourcing agents stop at the factory gate. We don't. Our freight desk in Ningbo and Shanghai negotiates rates against live market data, consolidates across multiple factories, produces CO, FORM-E and destination-compliant documentation, and hands off to your CHA with zero ambiguity.
            </p>
            <ul className="space-y-3 mb-10">
              {["FCL, LCL and air-freight coordination", "Multi-origin container consolidation", "Full export documentation desk", "Destination compliance pre-validation"].map(t => (
                <li key={t} className="flex items-center gap-3 text-white/80 text-[15px]">
                  <CheckCircle2 size={18} className="text-[#B8965A]"/> {t}
                </li>
              ))}
            </ul>
            <Link to="/services/export-logistics" className="btn-outline">Explore Logistics <ArrowRight size={16}/></Link>
          </div>
        </div>
      </section>

      {/* 8. CLUSTERS PREVIEW */}
      <section className="bg-white py-20" data-testid="clusters-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-6">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-4">
                <span className="gold-rule"></span>
                <span className="eyebrow text-[#1C3E5F]">Manufacturing Clusters</span>
              </div>
              <h2 className="font-serif text-[#1C3E5F] text-4xl lg:text-5xl leading-[1.05]">Every category in China<br/>is produced in a cluster.</h2>
            </div>
            <Link to="/clusters" className="link-underline text-[#1C3E5F] text-sm uppercase tracking-[0.18em] font-semibold whitespace-nowrap">Explore All {CLUSTERS.length} <ArrowRight size={14} className="inline"/></Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {CLUSTERS.slice(0, 10).map((c) => (
              <Link key={c.city} to="/clusters" className="bg-white p-5 hover:bg-[#F7F8FA] transition-colors group" data-testid={`home-cluster-${c.city.replace(/[··'\s]/g, '')}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="font-serif text-[#1C3E5F] text-xl group-hover:text-[#B8965A] transition-colors">{c.city}</div>
                  <MapPin size={12} className="text-[#B8965A] mt-1.5"/>
                </div>
                <div className="flex flex-wrap gap-1 mb-2">
                  {c.tags.slice(0, 2).map(t => <span key={t} className="text-[9px] uppercase tracking-[0.1em] bg-[#F7F8FA] text-[#1C3E5F] px-1.5 py-0.5 border border-[#E2E8F0] font-semibold">{t}</span>)}
                </div>
                <div className="text-[11px] text-[#1C3E5F]/60 line-clamp-2 leading-snug">{c.note}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 9. CHINA BUSINESS TRIPS */}
      <section className="bg-[#F8FAFC] py-20" data-testid="trips-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-rule"></span>
              <span className="eyebrow text-[#1C3E5F]">China Business Trips</span>
            </div>
            <h2 className="font-serif text-[#1C3E5F] text-5xl lg:text-6xl leading-[1.05] mb-8">A hosted week in China replaces six months of email.</h2>
            <p className="text-[#1C3E5F]/70 text-lg leading-relaxed mb-8">
              We run fully-hosted sourcing tours for serious buyers: visa invitation, airport pickup, senior interpreter, pre-qualified factory visit schedule, Canton Fair booth-by-booth navigation, and evening business dinners.
            </p>
            <Link to="/china-business-trips" className="btn-dark">Plan a China Visit <ArrowRight size={16}/></Link>
          </div>
          <div className="lg:col-span-6">
            <ResponsiveImg srcId={IMG_ID.factory} alt="Factory visit" className="w-full aspect-[5/4] object-cover" />
          </div>
        </div>
      </section>

      {/* 10. TESTIMONIALS */}
      <TestimonialsBlock />

      {/* 10b. CASE STUDIES */}
      <section className="bg-[#F8FAFC] py-20" data-testid="case-studies">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-12 gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4"><span className="gold-rule"/><span className="eyebrow text-[#1C3E5F]">Case Studies</span></div>
              <h2 className="font-serif text-[#1C3E5F] text-5xl lg:text-6xl leading-[1.05] max-w-3xl">Operational outcomes,<br/>not testimonials.</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {caseStudies.map((cs, i) => (
              <article key={cs.id || i} className="bg-white p-8 lg:p-10 hover:bg-[#FFF7E5]/40 transition-colors" data-testid={`case-${i}`}>
                <div className="eyebrow text-[#B8965A] mb-2">{cs.data.industry} · {cs.data.destination}</div>
                <h3 className="font-serif text-[#1C3E5F] text-2xl lg:text-3xl leading-tight mb-4">{cs.data.title}</h3>
                <p className="text-[#1C3E5F]/75 text-[14.5px] leading-relaxed mb-5">{cs.data.summary}</p>
                <ul className="space-y-1.5">
                  {(cs.data.outcomes || []).map((o, j) => (
                    <li key={j} className="flex items-start gap-2 text-[12.5px] text-[#1C3E5F]/85">
                      <CheckCircle2 size={14} className="text-[#B8965A] mt-0.5 shrink-0"/>{o}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* 10c. FREIGHT TICKER — live 3-container matrix backed by /api/freight/rates */}
      {freight?.featured?.length > 0 && (
      <section className="bg-[#12283F] py-16" data-testid="freight-ticker">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-10 gap-6">
            <div>
              <div className="flex items-center gap-3 mb-3"><span className="gold-rule"/><span className="eyebrow text-[#B8965A]">China Outbound · Indicative Freight</span></div>
              <h2 className="font-serif text-white text-4xl lg:text-5xl leading-tight">Indicative ocean rates<br/>from China to the world.</h2>
              <p className="text-white/55 text-sm mt-3 max-w-xl">
                All-in indicative USD rates per container, actual quotes depend on origin port, season, carrier and current market conditions.
                {freight.last_updated && (
                  <> Updated <strong className="text-[#B8965A]">{new Date(freight.last_updated).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</strong> · {freight.total_corridors} corridors live.</>
                )}
              </p>
            </div>
            <Link to="/tools/ocean-rates" data-testid="freight-tile-viewall" className="hidden md:inline-flex items-center gap-1.5 text-[#B8965A] text-[11px] uppercase tracking-[0.16em] font-semibold whitespace-nowrap">
              <TrendingUp size={14}/> View all + container specs
            </Link>
          </div>
          <div className="border border-[#B8965A]/15">
            <div className="grid grid-cols-12 bg-[#1C3E5F] text-[#B8965A] text-[10px] uppercase tracking-[0.16em] py-3 px-5">
              <div className="col-span-5">Corridor</div>
              <div className="col-span-2 text-right">20&#39;GP</div>
              <div className="col-span-2 text-right">40&#39;GP</div>
              <div className="col-span-2 text-right">40&#39;HQ</div>
              <div className="col-span-1 text-right">Transit</div>
            </div>
            {freight.featured.map((f, i) => (
              <div key={f.id || i} className="grid grid-cols-12 py-3 px-5 text-white/85 text-[13px] border-t border-[#B8965A]/10 hover:bg-[#1C3E5F]/60 transition-colors" data-testid={`freight-row-${i}`}>
                <div className="col-span-5 font-medium">{f.origin} <span className="text-white/40 mx-1">→</span> {f.destination}</div>
                <div className="col-span-2 text-right text-white/65">{f.twenty_gp != null ? `$${Number(f.twenty_gp).toLocaleString()}` : "—"}</div>
                <div className="col-span-2 text-right text-white/85">{f.forty_gp != null ? `$${Number(f.forty_gp).toLocaleString()}` : "—"}</div>
                <div className="col-span-2 text-right text-white">{f.forty_hq != null ? `$${Number(f.forty_hq).toLocaleString()}` : "—"}</div>
                <div className="col-span-1 text-right text-white/55 text-[11px]">{f.transit_days}d</div>
              </div>
            ))}
          </div>
          <div className="mt-4 text-[10px] text-white/40">Rates in USD per container · Includes THC and BAF · Excludes local charges, duties and last-mile trucking · Actual booking rate confirmed on quotation</div>
        </div>
      </section>
      )}

      {/* 10d. FOUNDER NOTE */}
      <section className="bg-white py-24" data-testid="founder-note">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-3">
            <Quote size={36} className="text-[#B8965A]"/>
          </div>
          <div className="md:col-span-9">
            <div className="eyebrow text-[#1C3E5F] mb-4">Note from the third generation</div>
            <h2 className="font-serif text-[#1C3E5F] text-4xl lg:text-5xl leading-tight mb-6">Some businesses ship product. We ship trust.</h2>
            <p className="text-[#1C3E5F]/75 text-[17px] leading-relaxed mb-3">
              My grandfather started Fortune Imports &amp; Exports in 1991 with a single trade route and a notebook. My father took it across borders and taught me that on-ground discipline is the only thing that survives across decades.
            </p>
            <p className="text-[#1C3E5F]/75 text-[17px] leading-relaxed mb-6">
              Today we run the same playbook with a global client base, only with better technology, more cluster coverage, and the same family signature on every container we seal.
            </p>
            <div className="flex items-center gap-4 pt-2">
              <div className="font-serif text-[#B8965A] text-2xl italic">Director</div>
              <div className="h-px flex-1 bg-[#E2E8F0]"/>
              <a href="/api/cms/company-profile.pdf" className="inline-flex items-center gap-1.5 text-[#1C3E5F] text-[11px] uppercase tracking-[0.16em] font-semibold hover:text-[#B8965A]" data-testid="company-profile-cta">
                <Download size={12}/> Company Profile PDF
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 11. FINAL CTA */}
      <section className="bg-[#1C3E5F] py-24 relative overflow-hidden" data-testid="final-cta">
        <ResponsiveImg srcId={IMG_ID.warehouse} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="gold-rule"></span>
            <span className="eyebrow text-[#B8965A]">Ready To Begin</span>
            <span className="gold-rule"></span>
          </div>
          <h2 className="font-serif text-white text-5xl lg:text-7xl leading-[1.02] mb-10">
            Scale your sourcing with a partner whose family has been in import &amp; export for three generations.
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/submit-rfq" className="btn-primary">Submit Your RFQ <ArrowRight size={16}/></Link>
            <Link to="/book-consultation" className="btn-outline">Book 30-min Call</Link>
          </div>
        </div>
      </section>

      {/* 12. INLINE CALENDLY BOOKING WIDGET — buyers book without leaving the site */}
      <section className="bg-[#F8FAFC] py-20" data-testid="calendly-section">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="gold-rule"/>
            <span className="eyebrow text-[#B8965A]">Book Right Here</span>
          </div>
          <h2 className="font-serif text-[#1C3E5F] text-4xl lg:text-5xl leading-[1.05] mb-3">
            Pick a slot that works — no phone tag.
          </h2>
          <p className="text-[#6B7280] text-lg max-w-2xl mb-8">
            A senior sourcing lead will personally take the call. Thirty minutes,
            no obligation — English or Hindi.
          </p>
          <CalendlyInline testId="home-calendly-inline" />
        </div>
      </section>
    </div>
  );
}


// -----------------------------------------------------------------------------
// TestimonialsBlock — 60+ client voices with region filter + "Show more" toggle.
// -----------------------------------------------------------------------------
function TestimonialsBlock() {
  const [region, setRegion] = React.useState("all");
  const [showAll, setShowAll] = React.useState(false);

  const regionOf = (t) => {
    const c = (t.company || "").toLowerCase();
    if (/\bindia\b/.test(c)) return "india";
    if (/\bnepal\b/.test(c)) return "nepal";
    if (/(uae|ksa|saudi|kuwait|qatar|bahrain|oman|dubai|riyadh|doha|jeddah|abu dhabi|sharjah|manama|muscat)/.test(c)) return "middle-east";
    if (/(usa|united states|new york|california|los angeles|chicago|austin|miami|denver|portland|brooklyn|long beach|cleveland|san francisco)/.test(c)) return "usa";
    if (/canada/.test(c)) return "canada";
    if (/(germany|uk|united kingdom|france|italy|spain|netherlands|london|paris|berlin|hamburg|amsterdam|milan|barcelona)/.test(c)) return "europe";
    return "other";
  };
  const REGIONS = [
    { k: "all",         label: "All Regions" },
    { k: "india",       label: "India" },
    { k: "nepal",       label: "Nepal" },
    { k: "middle-east", label: "Middle East" },
    { k: "usa",         label: "USA" },
    { k: "canada",      label: "Canada" },
    { k: "europe",      label: "Europe" },
    { k: "other",       label: "Other" },
  ];
  const filtered = TESTIMONIALS.filter(
    (t) => region === "all" || regionOf(t) === region
  );
  const visible = showAll ? filtered : filtered.slice(0, 6);

  return (
    <section className="bg-white py-20" data-testid="testimonials-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="gold-rule"></span>
          <span className="eyebrow text-[#1C3E5F]">Client Voices</span>
          <span className="ml-auto text-xs text-[#1C3E5F]/55 uppercase tracking-widest">
            {TESTIMONIALS.length}+ voices from {new Set(TESTIMONIALS.map(regionOf)).size} regions
          </span>
        </div>
        <p className="max-w-2xl text-[#1C3E5F]/70 text-[15px] mb-8">
          A live sample of what clients across India, Nepal, the Middle East, USA, Canada and Europe say about working with Fortune Sourcings. Filter by region.
        </p>

        <div className="flex flex-wrap gap-2 mb-10" data-testid="testimonial-region-filter">
          {REGIONS.map((r) => (
            <button
              key={r.k}
              type="button"
              onClick={() => { setRegion(r.k); setShowAll(false); }}
              data-testid={`region-chip-${r.k}`}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] border transition-colors ${
                region === r.k
                  ? "bg-[#1C3E5F] text-white border-[#1C3E5F]"
                  : "bg-white text-[#1C3E5F] border-[#E2E8F0] hover:border-[#B8965A]"
              }`}
            >
              {r.label}
              <span className="ml-1.5 opacity-60">
                {r.k === "all" ? TESTIMONIALS.length : TESTIMONIALS.filter((t) => regionOf(t) === r.k).length}
              </span>
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-6" data-testid="testimonials-grid">
          {visible.map((t, i) => (
            <div
              key={i}
              className="flex flex-col gap-4 p-6 border border-[#E2E8F0] hover:border-[#B8965A] transition-colors"
              data-testid="testimonial-card"
            >
              <div className="text-4xl font-serif text-[#B8965A] leading-none">&ldquo;</div>
              <p className="text-[#1C3E5F]/85 text-[14.5px] leading-relaxed">{t.quote}</p>
              <div className="pt-3 border-t border-[#E2E8F0] mt-auto">
                <div className="text-[#1C3E5F] text-sm font-semibold">{t.name}</div>
                <div className="text-xs text-[#1C3E5F]/60 mt-1">{t.company}</div>
              </div>
            </div>
          ))}
        </div>

        {filtered.length > 6 && (
          <div className="mt-8 flex justify-center">
            <button
              onClick={() => setShowAll((s) => !s)}
              data-testid="toggle-show-all-testimonials"
              className="btn-outline !py-2.5 !px-6 !text-[11px]"
            >
              {showAll ? "Collapse" : `Show all ${filtered.length} voices`}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
